import { Form } from "antd";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreatePlant, useUpdatePlant } from "../hooks/usePlants";
import { usePlantsStore } from "../store/plants.store";
import type { CreatePlantRequest } from "../types";
import { PlantForm } from "./PlantForm";

interface PlantFormModalProps {
	organizationId: string;
}

export function PlantFormModal({ organizationId }: PlantFormModalProps) {
	const [form] = Form.useForm<CreatePlantRequest>();
	const { modalOpen, editingItem, defaultOrganizationId, closeModal } = usePlantsStore();

	const orgId = editingItem?.organizationId ?? defaultOrganizationId ?? organizationId;

	const { mutate: create, isPending: isCreating } = useCreatePlant();
	const { mutate: update, isPending: isUpdating } = useUpdatePlant();

	const close = () => {
		form.resetFields();
		closeModal();
	};

	useEffect(() => {
		if (modalOpen) {
			if (editingItem) {
				form.setFieldsValue({
					name: editingItem.name,
					type: editingItem.type,
					description: editingItem.description ?? undefined,
					organizationId: editingItem.organizationId,
				});
			} else {
				form.resetFields();
				form.setFieldsValue({ organizationId: orgId ?? undefined });
			}
		}
	}, [modalOpen, editingItem, orgId, form]);

	const onFinish = (values: CreatePlantRequest) => {
		if (editingItem) {
			update(
				{
					id: editingItem.id,
					data: {
						name: values.name,
						type: values.type,
						description: values.description ?? null, // allow clearing
					},
				},
				{ onSuccess: close }
			);
		} else {
			create(
				{
					name: values.name,
					type: values.type,
					description: values.description,
					organizationId: values.organizationId ?? organizationId,
				},
				{ onSuccess: close }
			);
		}
	};

	const handleOk = () => {
		form
			.validateFields()
			.then(onFinish)
			.catch(() => {
				// Validation failed - Form shows errors inline
			});
	};

	return (
		<FormModal
			title="Yangi ekin"
			editTitle="Ekinni tahrirlash"
			editing={!!editingItem}
			open={modalOpen}
			onCancel={close}
			onOk={handleOk}
			confirmLoading={isCreating || isUpdating}
			width={640}
		>
			<PlantForm form={form} />
		</FormModal>
	);
}
